import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ChevronRight } from "lucide-react";

export default function TutorOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [opts, setOpts] = useState({ grades: [], provinces: [], subjects: [] });
  const [form, setForm] = useState({
    grade: "", province: "", subjects: [],
    school_name: "", parent_name: "", parent_email: "",
    parent_digest_opt_in: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/tutor/profile").then(({ data }) => {
      setOpts(data.options);
      if (data.profile.onboarded) nav("/tutor", { replace: true });
      setForm((f) => ({ ...f, ...data.profile }));
    }).catch(() => toast.error("Please sign in first"));
    // load-on-mount; setters/api stable, no need to re-run on each render
  }, [nav]);

  const toggleSubject = (s) => {
    setForm((f) => ({
      ...f,
      subjects: f.subjects.includes(s) ? f.subjects.filter((x) => x !== s) : [...f.subjects, s],
    }));
  };

  const submit = async () => {
    if (!form.grade || !form.province || form.subjects.length === 0) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/api/tutor/profile", form);
      toast.success("Welcome to Teacher4Me!");
      nav("/tutor");
    } catch (e) {
      toast.error("Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="tutor-onboarding">
      <div className="label-eyebrow flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#D92D20]" /> Teacher4Me onboarding</div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-1">Let&apos;s set up your learning.</h1>
      <p className="text-[#4B5563] mt-2">Step {step} of 3</p>
      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6">
        {step === 1 && (
          <div data-testid="onboard-step-1">
            <h2 className="font-display font-bold text-xl">Pick your grade & province</h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-[#111827]">Grade</label>
                <select data-testid="onboard-grade" value={form.grade}
                  onChange={(e) => setForm({ ...form, grade: e.target.value })}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                  <option value="">Select grade…</option>
                  {opts.grades.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-[#111827]">Province</label>
                <select data-testid="onboard-province" value={form.province}
                  onChange={(e) => setForm({ ...form, province: e.target.value })}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
                  <option value="">Select province…</option>
                  {opts.provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-sm font-semibold text-[#111827]">School (optional)</label>
              <input data-testid="onboard-school" value={form.school_name}
                onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. Sir John A. Macdonald HS" />
            </div>
            <button data-testid="onboard-next-1" onClick={() => setStep(2)}
              disabled={!form.grade || !form.province}
              className="btn-primary mt-6 disabled:opacity-50 flex items-center gap-1">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboard-step-2">
            <h2 className="font-display font-bold text-xl">Pick your subjects</h2>
            <p className="text-sm text-[#4B5563] mt-1">Choose all that apply — you can change these later.</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-2">
              {opts.subjects.map((s) => {
                const active = form.subjects.includes(s);
                return (
                  <button key={s} data-testid={`subject-${s}`} onClick={() => toggleSubject(s)}
                    className={`text-left text-sm px-3 py-2 rounded-lg border transition-colors ${
                      active ? "border-[#D92D20] bg-[#FEF2F2] text-[#D92D20] font-semibold"
                             : "border-[#E5E7EB] hover:border-[#9CA3AF]"
                    }`}>{s}</button>
                );
              })}
            </div>
            <div className="flex justify-between mt-6">
              <button data-testid="onboard-back-2" onClick={() => setStep(1)} className="btn-outline">Back</button>
              <button data-testid="onboard-next-2" onClick={() => setStep(3)}
                disabled={form.subjects.length === 0}
                className="btn-primary disabled:opacity-50">Next</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="onboard-step-3">
            <div className="bg-gradient-to-br from-[#FEF2F2] to-white border-2 border-[#FECACA] rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <div className="text-4xl">📧</div>
                <div>
                  <h2 className="font-display font-black text-xl">Add a parent email to receive learning reports and progress updates.</h2>
                  <p className="text-sm text-[#4B5563] mt-1">
                    <b>Highly recommended.</b> Every Sunday, parents get a beautiful PDF summary with subjects studied, quiz scores, study time, strengths, and areas needing improvement.
                  </p>
                  <ul className="text-xs text-[#4B5563] mt-3 space-y-1">
                    <li>✓ Weekly progress reports with charts</li>
                    <li>✓ Achievement notifications when milestones unlock</li>
                    <li>✓ Strengths &amp; gaps analysis</li>
                    <li>✓ One-click unsubscribe anytime</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold">Parent name</label>
                <input data-testid="onboard-parent-name" value={form.parent_name}
                  placeholder="e.g. Priya Sharma"
                  onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                  className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-sm font-semibold">Parent email <span className="text-[10px] uppercase tracking-widest text-[#D92D20] bg-[#FEF2F2] px-1.5 py-0.5 rounded ml-1">Recommended</span></label>
                <input type="email" data-testid="onboard-parent-email" value={form.parent_email}
                  placeholder="parent@example.com"
                  onChange={(e) => setForm({ ...form, parent_email: e.target.value })}
                  className="mt-1 w-full border-2 border-[#FECACA] focus:border-[#D92D20] rounded-lg px-3 py-2 text-sm outline-none transition-colors" />
              </div>
            </div>
            <label className="flex items-start gap-2 mt-4 cursor-pointer">
              <input type="checkbox" data-testid="onboard-parent-digest"
                checked={form.parent_digest_opt_in}
                onChange={(e) => setForm({ ...form, parent_digest_opt_in: e.target.checked })}
                className="mt-1" />
              <span className="text-sm text-[#4B5563]">
                Email parents a weekly progress report every Sunday. Unsubscribe in one click anytime.
              </span>
            </label>
            <div className="flex justify-between mt-6">
              <button data-testid="onboard-back-3" onClick={() => setStep(2)} className="btn-outline">Back</button>
              <div className="flex gap-2">
                <button data-testid="onboard-skip-parent" onClick={() => setForm({ ...form, parent_name: "", parent_email: "" }) || submit()}
                  disabled={saving}
                  className="text-sm text-[#4B5563] underline hover:text-[#111827]">
                  Skip for now
                </button>
                <button data-testid="onboard-finish" onClick={submit} disabled={saving}
                  className="btn-primary disabled:opacity-50">
                  {saving ? "Saving…" : "Finish & start learning"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
